import * as R from 'ramda';
import { compose, withState, withHandlers, lifecycle } from 'react-recompose';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
import { asyncGetConfigsByNames } from '../../../helpers/api-async';
// utilities
import { sendRequest } from '../../../utilities/http';
import endpointsMap from '../../../utilities/endpoints';
// components charge
import { getAccessorialsForState } from '../helpers';
import { withContractChargeAccessorials } from './with-contract-charge-accessorials';
import { setAccessorialsFromResponseToChargeUse, getRateFromSharedAccessAndFuelPrices } from './helpers';
//////////////////////////////////////////////////

const mapDefaultAccessorial = (type, configValue, accessorials) => {
  const sharedAccessorial = R.find(
    R.propEq(configValue, GC.FIELD_GUID),
    R.values(accessorials),
  );

  const accessorialConfig = R.find(
    R.propEq(configValue, GC.FIELD_ORIGINAL_CONFIG_GUID),
    R.values(accessorials),
  );

  const data = R.or(sharedAccessorial, accessorialConfig);

  if (G.isNilOrEmpty(data)) return null;

  const {
    rate,
    guid,
    currency,
    rateType,
    rateUnit,
    matrixUom,
    nonTaxable,
    matrixType,
    fuelRelated,
    storedValue,
    fuelIndexInfo,
    advancePayment,
    displayedValue,
    internalExpense,
    matrixVariables,
    originalConfigGuid,
  } = data;

  return {
    guid,
    type,
    rate,
    total: 0,
    currency,
    rateType,
    rateUnit,
    matrixUom,
    nonTaxable,
    matrixType,
    fuelRelated,
    storedValue,
    fuelIndexInfo,
    displayedValue,
    advancePayment,
    internalExpense,
    matrixVariables,
    [GC.FIELD_ID]: G.genShortId(),
    [GC.FIELD_ACCESSORIAL_CONFIG_GUID]: originalConfigGuid,
  };
};

const getItemsFromEvents = R.compose(
  R.chain((event) => R.or(
    R.path(['formData', GC.FIELD_LOAD_ITEMS], event),
    R.propOr([], GC.FIELD_LOAD_ITEMS, event),
  )),
  R.values,
);

const getItemIdentifier = (item) => R.or(
  G.getGuidFromObject(item),
  R.prop(GC.FIELD_ITEM_INTERNAL_ID, item),
);

const isSameRateItem = (item1, item2) => {
  const item1Identifier = getItemIdentifier(item1);
  const item2Identifier = getItemIdentifier(item2);

  if (G.isAllNotNilOrNotEmpty([item1Identifier, item2Identifier])) {
    return R.equals(item1Identifier, item2Identifier);
  }

  return R.equals(item1, item2);
};

export const getRateItemsFromProps = (props) => R.compose(
  R.uniqWith(isSameRateItem),
  R.filter(G.isObject),
  R.reduce(R.concat, []),
)([
  R.propOr([], GC.FIELD_LOAD_ITEMS, props),
  R.pathOr([], ['clo', GC.FIELD_LOAD_ITEMS], props),
  R.pathOr([], ['details', GC.FIELD_LOAD_ITEMS], props),
  R.pathOr([], ['loadData', GC.FIELD_LOAD_ITEMS], props),
  R.pathOr([], ['selectedRate', GC.FIELD_LOAD_ITEMS], props),
  R.pathOr([], ['initialValues', GC.FIELD_LOAD_ITEMS], props),
  R.pathOr([], ['tel', GC.FIELD_LOAD_ITEMS], props),
  R.pathOr([], ['asyncInitialData', 'data', GC.FIELD_LOAD_ITEMS], props),
  getItemsFromEvents(R.propOr([], 'events', props)),
  getItemsFromEvents(R.propOr([], 'telEvents', props)),
  getItemsFromEvents(R.propOr([], 'stops', props)),
  getItemsFromEvents(R.pathOr([], ['loadData', 'events'], props)),
  getItemsFromEvents(R.pathOr([], ['tel', GC.FIELD_LOAD_STOPS], props)),
]);

const getItemLengthInMatrixUom = (item, matrixUom) => {
  const itemLengthValue = R.prop(GC.FIELD_ITEM_DIMENSIONS_LENGTH, item);

  const dimensionUomMap = {
    [GC.UOM_FEET]: GC.UOM_FOOT,
    [GC.UOM_INCHES]: GC.UOM_INCH,
    [GC.UOM_METERS]: GC.UOM_METER,
    [GC.UOM_CENTIMETERS]: GC.UOM_CENTIMETER,
  };

  const normalizeDimensionUom = (uom) => R.propOr(uom, uom, dimensionUomMap);
  const itemDimensionsUom = normalizeDimensionUom(R.prop(GC.FIELD_ITEM_DIMENSIONS_UOM, item));
  const matrixDimensionsUom = normalizeDimensionUom(matrixUom);
  const supportedUoms = [GC.UOM_FOOT, GC.UOM_INCH, GC.UOM_METER, GC.UOM_CENTIMETER];

  if (G.isNilOrEmpty(itemLengthValue)) return null;

  const itemLength = Number(itemLengthValue);

  if (R.not(Number.isFinite(itemLength))) return null;

  if (R.equals(itemDimensionsUom, matrixDimensionsUom)) return itemLength;

  if (R.any((uom) => R.not(R.includes(uom, supportedUoms)), [itemDimensionsUom, matrixDimensionsUom])) return null;

  return G.convertDimensionValueByUoms(itemLength, itemDimensionsUom, matrixDimensionsUom);
};

const isMatrixVariableForItemLength = (itemLength, { valueFrom, valueTo }) => {
  const withFrom = G.isNotNilAndNotEmpty(valueFrom);
  const withTo = G.isNotNilAndNotEmpty(valueTo);

  if (R.not(R.or(withFrom, withTo))) return false;

  const lowerMatches = R.or(R.not(withFrom), R.lt(Number(valueFrom), itemLength));
  const upperMatches = R.or(R.not(withTo), R.gte(Number(valueTo), itemLength));

  return R.and(lowerMatches, upperMatches);
};

const getMatrixItemInfo = R.pick([
  GC.FIELD_ITEM_ID,
  GC.FIELD_ITEM_WEIGHT,
  GC.FIELD_ITEM_QUANTITY,
  GC.FIELD_ITEM_WEIGHT_TYPE,
  GC.FIELD_ITEM_PACKAGE_TYPE,
  GC.FIELD_ITEM_INTERNAL_ID,
]);

export const getItemLengthMatrixAccessorials = (accessorials, items) => R.chain((accessorial) => {
  const matrixUom = R.prop(GC.FIELD_ACCESSORIAL_MATRIX_UOM, accessorial);
  const matrixVariables = R.propOr([], GC.FIELD_ACCESSORIAL_MATRIX_VARIABLES, accessorial);

  return R.compose(
    R.reject(R.isNil),
    R.map((item) => {
      const itemLength = getItemLengthInMatrixUom(item, matrixUom);

      if (R.isNil(itemLength)) return null;

      const matrixVariable = R.find(
        (variable) => isMatrixVariableForItemLength(itemLength, variable),
        matrixVariables,
      );

      if (G.isNilOrEmpty(matrixVariable)) return null;

      return R.pipe(
        R.assoc(GC.FIELD_CHARGE_RATE, R.prop(GC.FIELD_CHARGE_RATE, matrixVariable)),
        R.assoc(GC.FIELD_ACCESSORIAL_MATRIX_ITEM_INFO, getMatrixItemInfo(item)),
      )(accessorial);
    }),
  )(items);
}, R.filter(
  R.propEq(GC.ACCESSORIAL_MATRIX_TYPE_ITEM_LENGTH, GC.FIELD_ACCESSORIAL_MATRIX_TYPE),
  R.or(accessorials, []),
));

export const mapDefaultAccessorials = (
  defaultAccessorials,
  accessorials,
  autoAssessorial,
) => R.compose(
  R.reject(R.isNil),
  R.map((item) => {
    const {
      guid,
      currency,
      rateType,
      minStops,
      maxStops,
      rateUnit,
      deduction,
      matrixUom,
      matrixType,
      useFuelIndex,
      freeDistance,
      matrixItemInfo,
      assessorialGuid,
      matrixVariables,
      transportationMode,
    } = item;

    const { rate, fuelIndexInfo } = getRateFromSharedAccessAndFuelPrices(item);

    const accessorialConfig = R.find(R.propEq(assessorialGuid, GC.FIELD_ORIGINAL_CONFIG_GUID), accessorials);

    if (G.isNilOrEmpty(accessorialConfig)) return null;

    const {
      nonTaxable,
      fuelRelated,
      storedValue,
      advancePayment,
      displayedValue,
      internalExpense,
    } = accessorialConfig;

    const type = G.ifElse(
      R.or(useFuelIndex, fuelRelated),
      GC.CHARGE_TYPE_FUEL,
      GC.CHARGE_TYPE_ADDITIONAL,
    );

    return {
      guid,
      type,
      rate,
      total: 0,
      currency,
      rateType,
      rateUnit,
      minStops,
      maxStops,
      deduction,
      matrixUom,
      nonTaxable,
      matrixType,
      fuelRelated,
      storedValue,
      freeDistance,
      fuelIndexInfo,
      matrixItemInfo,
      displayedValue,
      advancePayment,
      internalExpense,
      autoAssessorial,
      matrixVariables,
      [GC.FIELD_ID]: G.genShortId(),
      defaultAccessorial: G.isNilOrEmpty(autoAssessorial),
      [GC.FIELD_ACCESSORIAL_CONFIG_GUID]: assessorialGuid,
      [GC.FIELD_TRANSPORTATION_MODE_GUID]: G.getDropdownOptionGuidFromObject(transportationMode),
    };
  }),
)(defaultAccessorials);

const getDefaultCarrierAccessorials = async (branchGuid, accessorials, callback) => {
  try {
    const defaultCarrierAccessorials = R.path(['amousDefaultCarrierAccessorials', branchGuid], window);

    if (G.isNilOrEmpty(defaultCarrierAccessorials)) {
      const res = await sendRequest(
        'get',
        endpointsMap.defaultCarrierAccessorialList,
        { params: { [GC.BRANCH_GUID]: branchGuid } },
      );

      const { data, status } = res;

      if (G.isResponseSuccess(status)) {
        const newAmousDefaultCarrierAccessorials = R.assoc(
          branchGuid,
          data,
          G.getItemFromWindow('amousDefaultCarrierAccessorials'),
        );

        const mapped = mapDefaultAccessorials(data, accessorials);

        G.callFunctionWithArgs(callback, mapped);

        G.setItemToWindow('amousDefaultCarrierAccessorials', newAmousDefaultCarrierAccessorials);
      } else {
        G.handleException('getDefaultCarrierAccessorials exception');
      }
    } else {
      const mapped = mapDefaultAccessorials(defaultCarrierAccessorials, accessorials);

      G.callFunctionWithArgs(callback, mapped);
    }
  } catch (error) {
    G.handleException('error', 'getDefaultCarrierAccessorials exception');
  }
};

const getDefaultOrderAccessorials = async (branchGuid, accessorials, callback) => {
  try {
    const defaultAccessorials = R.path(['amousDefaultOrderAccessorials', branchGuid], window);

    if (G.isNilOrEmpty(defaultAccessorials)) {
      const res = await sendRequest(
        'get',
        endpointsMap.defaultOrderAccessorialList,
        { params: { [GC.BRANCH_GUID]: branchGuid } },
      );

      const { data, status } = res;

      if (G.isResponseSuccess(status)) {
        const newAccessorials = R.assoc(
          branchGuid,
          data,
          G.getItemFromWindow('amousDefaultOrderAccessorials'),
        );

        const mapped = mapDefaultAccessorials(data, accessorials);

        G.callFunctionWithArgs(callback, mapped);

        G.setItemToWindow('amousDefaultOrderAccessorials', newAccessorials);
      } else {
        G.handleException('getDefaultOrderAccessorials exception');
      }
    } else {
      const mapped = mapDefaultAccessorials(defaultAccessorials, accessorials);

      G.callFunctionWithArgs(callback, mapped);
    }
  } catch (error) {
    G.handleException('error', 'getDefaultOrderAccessorials exception');
  }
};

const getDefaultOrderFuelAccessorial = async (branchGuid, accessorials, callback) => {
  try {
    const data = await asyncGetConfigsByNames({ branchGuid, names: [GC.CLO_RATE_DEFAULT_FUEL_ASSESSORIAL] });

    const configValue = G.getConfigValueFromStore(GC.CLO_RATE_DEFAULT_FUEL_ASSESSORIAL, data);
    const mapped = mapDefaultAccessorial('fuel', configValue, accessorials);

    G.callFunctionWithArgs(callback, mapped);
  } catch (error) {
    G.handleException('error', 'getDefaultOrderFuelAccessorial exception');
  }
};

export const getAutoAccessorials = ({
  items,
  stopCount,
  applyToType,
  accessorials,
  autoAccessorials,
  matrixAccessorials,
}) => {
  const filtered = R.compose(
    R.filter(({ rateType, minStops }) => {
      if (R.and(R.equals(rateType, GC.CHARGE_RATE_TYPE_STOP), G.isNotNilAndNotEmpty(minStops))) {
        return R.gt(stopCount, minStops);
      }

      return true;
    }),
    R.reject(R.propEq(GC.ACCESSORIAL_MATRIX_TYPE_ITEM_LENGTH, GC.FIELD_ACCESSORIAL_MATRIX_TYPE)),
  )(R.or(autoAccessorials, []));

  const itemLengthMatrixAccessorials = R.compose(
    (matrixItems) => getItemLengthMatrixAccessorials(matrixItems, R.or(items, [])),
    R.uniqBy((item) => [
      R.prop(GC.FIELD_GUID, item),
      R.prop(GC.FIELD_ORIGINAL_CONFIG_GUID, item),
    ].join(':')),
    R.reduce(R.concat, []),
  )([
    R.or(autoAccessorials, []),
    R.filter(
      R.compose(R.includes(applyToType), R.propOr([], GC.FIELD_AUTO_APPLY_TO)),
      R.or(matrixAccessorials, []),
    ),
  ]);

  return mapDefaultAccessorials(R.concat(filtered, itemLengthMatrixAccessorials), accessorials, true);
};

export const getApplyToServicesFilterData = (loadData) => {
  if (R.not(R.has(GC.FIELD_SERVICES, R.or(loadData, {})))) return {};

  return {
    filterByServices: true,
    [GC.FIELD_SERVICE_GUIDS]: R.prop(GC.FIELD_SERVICES, loadData),
  };
};

export const getTelSharedAccessorialsApplyTo = async (props) => {
  try {
    const {
      telGuid,
      loadData,
      stopCount,
      branchGuid,
      applyToType,
      accessorials,
      matrixAccessorials,
      requestDriverRateData = sendRequest,
      items = getRateItemsFromProps(props),
    } = props;

    if (G.isNotNilAndNotEmpty(telGuid)) {
      const options = { params: { applyToType } };

      const endpoint = endpointsMap.getTelAutoApplySharedAssessorials(telGuid);

      const { data, status } = await requestDriverRateData('get', endpoint, options);

      if (G.isResponseSuccess(status)) {
        return getAutoAccessorials({
          items,
          stopCount,
          applyToType,
          accessorials,
          matrixAccessorials,
          autoAccessorials: R.or(data, []),
        });
      }
    }

    if (R.or(G.isNilOrEmpty(loadData), G.isNilOrEmpty(branchGuid))) return [];

    const origin = R.path(['events', 0, 'location'], loadData);
    const destination = R.path(['location'], R.last(R.pathOr([], ['events'], loadData)));

    if (R.or(G.isNilOrEmpty(origin), G.isNilOrEmpty(destination))) return [];

    const options = {
      data: {
        origin,
        destination,
        applyToType,
        [GC.BRANCH_GUID]: branchGuid,
        ...getApplyToServicesFilterData(loadData),
      },
    };

    const endpoint = endpointsMap.sharedAccessorialListApplyTo;

    const { data, status } = await requestDriverRateData('post', endpoint, options);

    if (G.isResponseSuccess(status)) {
      return getAutoAccessorials({
        items,
        stopCount,
        applyToType,
        accessorials,
        matrixAccessorials,
        autoAccessorials: R.or(data, []),
      });
    }

    return [];
  } catch (error) {
    G.handleException('error', 'getTelSharedAccessorialsApplyTo exception');
  }
};

const getSharedAccessorialsApplyTo = async (props) => {
  try {
    const {
      cloGuid,
      telGuid,
      loadData,
      callback,
      stopCount,
      branchGuid,
      applyToType,
      accessorials,
      matrixAccessorials,
      requestDriverRateData = sendRequest,
      items = getRateItemsFromProps(props),
    } = props;

    if (G.isNotNilAndNotEmpty(telGuid)) {
      const options = { params: { applyToType } };

      const endpoint = endpointsMap.getTelAutoApplySharedAssessorials(telGuid);

      const { data, status } = await requestDriverRateData('get', endpoint, options);

      if (G.isResponseSuccess(status)) {
        const autoAccessorials = getAutoAccessorials({
          items,
          stopCount,
          applyToType,
          accessorials,
          matrixAccessorials,
          autoAccessorials: data,
        });

        return G.callFunctionWithArgs(callback, autoAccessorials);
      }
    }

    if (G.isNotNilAndNotEmpty(cloGuid)) {
      const options = { params: { applyToType } };

      const endpoint = endpointsMap.getCloAutoApplySharedAssessorials(cloGuid);

      const { data, status } = await requestDriverRateData('get', endpoint, options);

      if (G.isResponseSuccess(status)) {
        const autoAccessorials = getAutoAccessorials({
          items,
          stopCount,
          applyToType,
          accessorials,
          matrixAccessorials,
          autoAccessorials: data,
        });

        return G.callFunctionWithArgs(callback, autoAccessorials);
      }
    }

    if (R.or(G.isNilOrEmpty(loadData), G.isNilOrEmpty(branchGuid))) return [];

    const origin = R.path(['events', 0, 'location'], loadData);
    const destination = R.path(['location'], R.last(R.pathOr([], ['events'], loadData)));

    if (R.or(G.isNilOrEmpty(origin), G.isNilOrEmpty(destination))) return [];

    const options = {
      data: {
        origin,
        destination,
        applyToType,
        [GC.BRANCH_GUID]: branchGuid,
        ...getApplyToServicesFilterData(loadData),
      },
    };

    const endpoint = endpointsMap.sharedAccessorialListApplyTo;

    const { data, status } = await requestDriverRateData('post', endpoint, options);

    if (G.isResponseSuccess(status)) {
      const autoAccessorials = getAutoAccessorials({
        items,
        stopCount,
        applyToType,
        accessorials,
        matrixAccessorials,
        autoAccessorials: data,
      });

      G.callFunctionWithArgs(callback, autoAccessorials);
    }
  } catch (error) {
    G.handleException('error', 'getSharedAccessorialsApplyTo exception');
  }
};

export const withAsyncChargeAccessorials = (args = {}) => compose(
  withState('accessorials', 'setAccessorials', null),
  withState('matrixAccessorials', 'setMatrixAccessorials', []),
  withState('fuelSelectOptions', 'setFuelSelectOptions', []),
  withState('noFuelSelectOptions', 'setNoFuelSelectOptions', []),
  withState('defaultOrderAccessorials', 'setDefaultOrderAccessorials', null),
  withState('sharedAccessorialsApplyTo', 'setSharedAccessorialsApplyTo', null),
  withState('defaultCarrierAccessorials', 'setDefaultCarrierAccessorials', null),
  withState('defaultOrderFuelAccessorial', 'setDefaultOrderFuelAccessorial', null),
  withHandlers({
    handleGetAccessorials: (props) => async () => {
      const {
        rate,
        events,
        cloGuid,
        telGuid,
        loadData,
        loadType,
        isInvoice,
        telEvents,
        stopCount,
        branchGuid,
        isFromPlanner,
        setAccessorials,
        setFuelSelectOptions,
        setMatrixAccessorials,
        setNoFuelSelectOptions,
        setDefaultOrderAccessorials,
        setSharedAccessorialsApplyTo,
        setDefaultCarrierAccessorials,
        setDefaultOrderFuelAccessorial,
        requestDriverRateData = sendRequest,
      } = props;

      const currentUserBranchGuid = G.getAmousCurrentUserBranchGuidFromWindow();
      const branchGuidToUse = R.or(branchGuid, currentUserBranchGuid);

      const requestedType = G.isFunction(args.accessorialType) ? args.accessorialType(props) : args.accessorialType;

      if (G.isFunction(args.accessorialType) && G.isNilOrEmpty(requestedType)) return;

      const accessorialType = requestedType
        || (args.isCustomer && GC.ACCESSORIAL_TYPE_CUSTOMER_RATE_INVOICE)
        || (args.isCarrier && GC.ACCESSORIAL_TYPE_CARRIER_RATE_INVOICE)
        || (args.isDriver && [
          GC.ACCESSORIAL_TYPE_VENDOR_RATE_INVOICE,
          GC.ACCESSORIAL_TYPE_DRIVER_RATE_PAYROLL,
        ].join(','));

      const options = {
        params: accessorialType
          ? { types: accessorialType, enterpriseGuid: branchGuidToUse }
          : { [GC.FIELD_BRANCH_GUID]: branchGuidToUse },
      };

      const accessorialsEndpoint = accessorialType
        ? endpointsMap.accessorialsListByTypes : endpointsMap.accessorialsListEndpoint;

      const sharedAccessorialsEndpoint = accessorialType
        ? endpointsMap.sharedAccessorialListByTypes : endpointsMap.sharedAccessorialList;

      const res = await requestDriverRateData('get', accessorialsEndpoint, options);
      const sharedRes = await requestDriverRateData('get', sharedAccessorialsEndpoint, options);

      const { status } = res;

      const data = R.or(res.data, []);

      if (G.isResponseSuccess(status)) {
        let accessorilasToUse = data;

        if (G.isTrue(isInvoice)) {
          accessorilasToUse = R.reject(R.propEq(true, GC.FIELD_CHARGE_INTERNAL_EXPENSE), accessorilasToUse);
        }

        const sharedAccessorials = G.getDataFromObject(sharedRes);
        const items = getRateItemsFromProps(props);
        const dataToUse = setAccessorialsFromResponseToChargeUse(accessorilasToUse, sharedAccessorials);

        const isDriver = G.isTrue(R.path(['isDriver'], args));
        const isCarrier = R.path(['isCarrier'], args);
        const isCustomer = R.path(['isCustomer'], args);
        const isCreateMode = R.path(['isCreateMode'], args);

        const applyToType = (isCustomer && GC.SHARED_ACCESSORIAL_AUTO_APPLY_TO_TYPE_CUSTOMER)
          || (isCarrier && GC.SHARED_ACCESSORIAL_AUTO_APPLY_TO_TYPE_CARRIER)
          || (isDriver && GC.SHARED_ACCESSORIAL_AUTO_APPLY_TO_TYPE_FLEET);

        const { accessorials, fuelSelectOptions, noFuelSelectOptions } = getAccessorialsForState(dataToUse, loadType, {
          grouped: isDriver || G.isTrue(isInvoice),
          includeContract: !isDriver && (!isInvoice || G.isTrue(args.isCustomer) || G.isTrue(args.isCarrier)),
        });

        setAccessorials(accessorials);
        setMatrixAccessorials(sharedAccessorials);
        setFuelSelectOptions(fuelSelectOptions);
        setNoFuelSelectOptions(noFuelSelectOptions);
        setSharedAccessorialsApplyTo(getAutoAccessorials({
          items,
          stopCount,
          applyToType,
          accessorials: data,
          autoAccessorials: [],
          matrixAccessorials: sharedAccessorials,
        }));

        if (R.and(G.isTrue(isCustomer), G.isTrue(isCreateMode))) {
          await getDefaultOrderFuelAccessorial(branchGuidToUse, accessorials, setDefaultOrderFuelAccessorial);

          getDefaultOrderAccessorials(branchGuidToUse, data, setDefaultOrderAccessorials);

          getSharedAccessorialsApplyTo({
            items,
            cloGuid,
            stopCount,
            accessorials: data,
            requestDriverRateData,
            branchGuid: branchGuidToUse,
            matrixAccessorials: sharedAccessorials,
            callback: setSharedAccessorialsApplyTo,
            applyToType: GC.SHARED_ACCESSORIAL_AUTO_APPLY_TO_TYPE_CUSTOMER,
            loadData: G.isNotNilAndNotEmpty(loadData)
              ? loadData
              : {
                [GC.FIELD_LOAD_STOPS]: R.or(events, []),
                [GC.FIELD_SERVICES]: R.propOr([], GC.FIELD_SERVICES, rate),
              },
          });
        }

        if (R.and(G.isTrue(isCarrier), G.isTrue(isCreateMode))) {
          getDefaultCarrierAccessorials(branchGuidToUse, data, setDefaultCarrierAccessorials);

          getSharedAccessorialsApplyTo({
            items,
            telGuid,
            loadData,
            stopCount,
            accessorials: data,
            requestDriverRateData,
            branchGuid: branchGuidToUse,
            matrixAccessorials: sharedAccessorials,
            callback: setSharedAccessorialsApplyTo,
            applyToType: GC.SHARED_ACCESSORIAL_AUTO_APPLY_TO_TYPE_CARRIER,
          });
        }

        if (R.and(G.isTrue(isDriver), G.isTrue(isCreateMode))) {
          const loadDataToUse = G.ifElse(
            R.and(G.isTrue(isFromPlanner), G.isNilOrEmpty(loadData)),
            R.objOf('events', telEvents),
            loadData,
          );

          getSharedAccessorialsApplyTo({
            items,
            telGuid,
            stopCount,
            accessorials: data,
            requestDriverRateData,
            loadData: loadDataToUse,
            branchGuid: branchGuidToUse,
            matrixAccessorials: sharedAccessorials,
            callback: setSharedAccessorialsApplyTo,
            applyToType: GC.SHARED_ACCESSORIAL_AUTO_APPLY_TO_TYPE_FLEET,
          });
        }
      }
    },
  }),
  lifecycle({
    UNSAFE_componentWillMount() {
      this.props.handleGetAccessorials();
    },
    componentDidUpdate(prevProps) {
      const accessorialTypeChanged = G.isFunction(args.accessorialType)
        && args.accessorialType(prevProps) !== args.accessorialType(this.props);

      const itemsChanged = R.not(R.equals(
        getRateItemsFromProps(prevProps),
        getRateItemsFromProps(this.props),
      ));

      if (R.or(accessorialTypeChanged, itemsChanged)) {
        this.props.handleGetAccessorials();
      }
    },
  }),
  withContractChargeAccessorials(args),
);
